'use client';

import React, { useState } from 'react';
import type { ReactNode } from 'react';

import dynamic from 'next/dynamic';
import type { ButtonBlock } from '@/types/pageContentTypes';

import ServiceHeroSection from './ServiceHeroSection';
import type { ServiceHeroSectionData } from './ServiceHeroSection.types';

import type { DefaultValues, FieldValues } from 'react-hook-form';
import type { ZodType } from 'zod';
import type { FieldConfig } from '@/types/forms';

const FormSubmissionModal = dynamic(
  () =>
    import('@/components/forms/FormSubmissionModal/FormSubmissionModal').then(
      (m) => ({ default: m.FormSubmissionModal }),
    ),
  { ssr: false },
);

export type ServiceHeroSectionWithEnquiryModalProps = {
  data: ServiceHeroSectionData;

  formId?: string;
  title?: ReactNode;
  description?: ReactNode;
  submitLabel?: string;

  metadata?: Record<string, unknown>;
  buildMetadata?: () => Record<string, unknown>;

  enquiryButtonMatcher?: (button: ButtonBlock, index: number) => boolean;

  modal?: {
    closeOnBackdrop?: boolean;
    closeOnEscape?: boolean;
    closeButtonLabel?: string;
    className?: string;
    panelClassName?: string;
    backdropClassName?: string;
    autoCloseOnSuccessMs?: number | false;
  };

  security?: {
    honeypot?: boolean;
    honeypotName?: string;
  };
};

const defaultMatcher = (button: ButtonBlock): boolean => {
  if (button.link === '/enquire') return true;
  if (button.content?.toLowerCase().includes('enquire')) return true;
  return false;
};

export function ServiceHeroSectionWithEnquiryModal({
  data,
  formId = 'serviceEnquiry',
  title = 'Send us an enquiry',
  description = 'Fill in your details and our team will contact you.',
  submitLabel = 'Send enquiry',
  metadata,
  buildMetadata,
  enquiryButtonMatcher = defaultMatcher,
  modal,
  security,
}: ServiceHeroSectionWithEnquiryModalProps) {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [pendingMetadata, setPendingMetadata] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [schemaData, setSchemaData] = useState<{
    schema: ZodType<FieldValues>;
    fields: FieldConfig[];
    defaultValues: DefaultValues<FieldValues>;
  } | null>(null);

  const handleButtonClick = (
    button: ButtonBlock,
    index: number,
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (!enquiryButtonMatcher(button, index)) return;

    event.preventDefault();

    const mergedMetadata: Record<string, unknown> = {
      source: 'service-hero',
      ...metadata,
      ...buildMetadata?.(),
    };

    setPendingMetadata(mergedMetadata);
    setHasBeenOpened(true);
    setEnquiryOpen(true);

    // Lazy-load schema on first open
    if (!schemaData) {
      import('@/features/enquiries/forms/serviceEnquirySchema').then((m) => {
        setSchemaData({
          schema: m.serviceEnquirySchema,
          fields: m.serviceEnquiryFields,
          defaultValues: m.serviceEnquiryDefaultValues,
        });
      });
    }
  };

  return (
    <>
      <ServiceHeroSection data={data} onButtonClick={handleButtonClick} />
      {hasBeenOpened && schemaData && (
        <FormSubmissionModal
          open={enquiryOpen}
          onOpenChange={setEnquiryOpen}
          formId={formId}
          schema={schemaData.schema}
          fields={schemaData.fields}
          defaultValues={schemaData.defaultValues}
          title={title}
          description={description}
          submitLabel={submitLabel}
          metadata={pendingMetadata ?? { source: 'service-hero' }}
          security={
            security ?? {
              honeypot: true,
              honeypotName: 'lips-name',
            }
          }
          modal={modal}
        />
      )}
    </>
  );
}
